// HAND A PROJECT TO ANOTHER TENANT.
//
// Owner, 2026-10-06: "a feature, to transfer a project and all their data and
// links and partners and attributes from 1 tenant to another tenant and this
// process should be done only by super admin". Owner, 2026-10-15 (relayed by
// cc-535d61): "the project transfer also added to super admin UI/workflow and
// partners transfer also in UI/workflow for super admin" - including the
// departing company becoming an active partner on the project.
//
// WHY IT IS A WALK OF STEPS. The first version sent only the project and the
// receiving company, so every preview came back blocked (no new owner, and
// usually no oversight entry) and "Transfer it" could never be pressed; the
// DP Electric transfer on 2026-10-11 had to be done by a script. Each step
// below is one decision the server refuses to make on its own:
//
//   1. WHERE IT GOES - the receiving company.
//   2. WHO OWNS IT AFTERWARDS - a named account of that company (somebody who
//      works in it through a second membership counts). Never picked for you.
//   3. HOW THE DEPARTING COMPANY KEEPS CONTROL - its oversight entry: which of
//      its people on the project, exactly one lead, and each member's keys,
//      defaulting to what they can do today (worked out by the server).
//      Skipped, and said so, when the project already has one with a lead.
//   4. THE DEPARTING COMPANY AS A PARTNER (optional) - who of its people go on
//      its partner row as supervisors (Show cost, job title) and who as users.
//      Written without mail, like the 2026-10-11 window did by hand.
//   5. THE PREVIEW - what moves, what stays with a partner, the pricing check,
//      what is copied, the keys the receiving company does NOT get, and if the
//      project is too big for a web request, the command that does it instead.
//   6. THE TYPED NAME, then the run, then the result with its Undo.
//
// The receiving company's licence is NOT raised by a transfer: the keys it
// lacks are listed, not granted. The platform's own keys stay with the
// platform, whatever the departing company held.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Button, Checkbox, Input, Modal, Popconfirm, Radio, Select, Steps, Switch, Table, Tag, message,
} from "antd";
import { previewTransfer, runTransfer, transferPeople, undoTransfer } from "api/tenantTransfer";
import { matchOption } from "components/SearchSelect/searchSelect";

const T = { muted: "#667085", line: "#EFEFEF" };
const n = (v) => Number(v || 0).toLocaleString();
const errText = (e, fallback) => (e && (e.err || e.message)) || fallback;
const STEPS = ["Receiving company", "New owner", "Oversight", "As a partner", "Preview", "Confirm"];

export default function TransferProjectDialog({ project, companies, fromCompanyId, onClose, onDone, defaultTo = "" }) {
  const [step, setStep] = useState(0);
  const [to, setTo] = useState(defaultTo);
  const [owner, setOwner] = useState("");
  const [people, setPeople] = useState([]);
  // Oversight entry being built: per departing person { on, same, keys }.
  const [ov, setOv] = useState({});
  const [lead, setLead] = useState("");
  // Partner row: on/off (null = not decided yet, take the default), and per
  // person { as: 'supervisor' | 'user' | 'none', showCost, jobTitle }.
  const [partnerOn, setPartnerOn] = useState(null);
  const [partner, setPartner] = useState({});
  const [typed, setTyped] = useState("");
  const [state, setState] = useState({ loading: false, plan: null, error: "" });
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [undoing, setUndoing] = useState(false);
  const seeded = useRef(false);

  const p = state.plan;
  const departing = (p && p.departing && p.departing.people) || [];
  const allKeys = (p && p.departing && p.departing.keys) || [];
  const ovExists = !!(p && p.oversight && p.oversight.exists);

  // THE DEFAULTS, ONCE, from the first preview that names the departing
  // company's people: everybody on the oversight entry, the project owner as
  // its lead, each with what they can do today; the departing company as a
  // partner when it has people on the project, its lead as supervisor (Show
  // cost on, Project Manager - the 2026-10-11 row) and the rest as users.
  useEffect(() => {
    if (seeded.current || !p || !p.departing) return;
    seeded.current = true;
    const list = p.departing.people || [];
    const first = list.find((d) => d.isProjectOwner) || list[0];
    setLead(first ? first.userId : "");
    setOv(Object.fromEntries(list.map((d) => [d.userId, { on: true, same: true, keys: d.permissions || [] }])));
    setPartnerOn(list.length > 0);
    setPartner(Object.fromEntries(list.map((d) => [d.userId, first && d.userId === first.userId
      ? { as: "supervisor", showCost: true, jobTitle: "Project Manager" }
      : { as: "user", showCost: false, jobTitle: d.jobTitle || "" }])));
  }, [p]);

  const choices = useMemo(() => {
    const out = {};
    if (owner) out.newOwner = owner;
    if (!ovExists && departing.length && lead) {
      out.oversight = {
        members: departing
          .filter((d) => d.userId === lead || (ov[d.userId] && ov[d.userId].on))
          .map((d) => {
            const o = ov[d.userId] || { same: true, keys: d.permissions };
            return { userId: d.userId, email: d.email, name: d.name, lead: d.userId === lead, permissions: o.same ? d.permissions : o.keys };
          }),
      };
    }
    if (partnerOn) {
      const sup = []; const usr = [];
      departing.forEach((d) => {
        const x = partner[d.userId];
        if (!x || x.as === "none") return;
        if (x.as === "supervisor") sup.push({ userId: d.userId, showCost: !!x.showCost, jobTitle: x.jobTitle || "" });
        else usr.push({ userId: d.userId, jobTitle: x.jobTitle || "" });
      });
      out.departingAsPartner = { supervisors: sup, users: usr };
    }
    return out;
  }, [owner, ovExists, departing, lead, ov, partnerOn, partner]);

  // THE PREVIEW IS RE-ASKED ON EVERY CHOICE, so what the person reads is the
  // plan for what they have picked - never a stale one.
  const choicesKey = JSON.stringify(choices);
  const look = useCallback(async () => {
    if (!project || !to) { setState({ loading: false, plan: null, error: "" }); return; }
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const plan = await previewTransfer(project._id, to, JSON.parse(choicesKey));
      setState({ loading: false, plan, error: "" });
    } catch (e) {
      setState({ loading: false, plan: null, error: errText(e, "Could not work that out") });
    }
  }, [project, to, choicesKey]);
  useEffect(() => {
    const t = setTimeout(look, 250);
    return () => clearTimeout(t);
  }, [look]);

  // Accounts of the receiving company, searched as the person types.
  const findPeople = useCallback(async (q) => {
    if (!to) { setPeople([]); return; }
    try {
      const r = await transferPeople(to, q || "");
      if (r && r.err) throw r;
      setPeople((r && r.people) || []);
    } catch (e) {
      message.error(errText(e, "Could not list that company's people"));
    }
  }, [to]);
  useEffect(() => { setOwner(""); findPeople(""); }, [findPeople]);

  const named = p && String(typed).trim() === String(p.project.name || "").trim();
  const partnerPicked = Object.values(partner).some((x) => x && x.as !== "none");
  const ovMembers = departing.filter((d) => d.userId === lead || (ov[d.userId] && ov[d.userId].on));
  const can = [
    !!to && !state.error,
    !!owner,
    ovExists || (!!lead && ovMembers.length > 0),
    !partnerOn || partnerPicked,
    !!p && !p.blocked && p.inline && !state.loading,
    !!p && !p.blocked && p.inline && named,
  ];

  const go = async () => {
    setRunning(true);
    try {
      const out = await runTransfer(project._id, to, typed, choices);
      setResult(out);
      message.success(`${project.title} now belongs to ${out.to.name}`);
      if (onDone) onDone(out);
    } catch (e) {
      // A REFUSAL IS NOT A FAILURE TO SHOW AS "something went wrong". The
      // server says what it is - too large for a request, blocked, the name
      // did not match, a line priced differently - and that is what the
      // person needs to read.
      message.error({ content: <span style={{ whiteSpace: "pre-wrap" }}>{errText(e, "The transfer did not run")}</span>, duration: 12 });
    } finally {
      setRunning(false);
    }
  };

  // UNDO RETURNS IT TO WHERE IT CAME FROM - the API takes that company.
  const undo = async () => {
    setUndoing(true);
    try {
      const out = await undoTransfer(project._id, result.from._id);
      message.success(`${project.title} is back with ${result.from.name}${out.removed && out.removed.partnerRow !== "none" ? ` (partner row ${out.removed.partnerRow})` : ""}`);
      if (onDone) onDone(out);
      onClose();
    } catch (e) {
      message.error(errText(e, "The undo did not run"));
    } finally {
      setUndoing(false);
    }
  };

  const setOvOf = (id, patch) => setOv((m) => ({ ...m, [id]: { ...(m[id] || { on: true, same: true, keys: [] }), ...patch } }));
  const setPartnerOf = (id, patch) => setPartner((m) => ({ ...m, [id]: { ...(m[id] || { as: "none" }), ...patch } }));

  const hazardBox = p && p.hazards && p.hazards.length ? (
    <Alert
      type={p.blocked ? "error" : "warning"} showIcon style={{ marginBottom: 10 }}
      message={p.blocked ? "This transfer cannot run yet" : "Read this before you go ahead"}
      description={(
        <div style={{ maxHeight: 160, overflow: "auto", whiteSpace: "pre-wrap" }}>
          {p.hazards.map((h, i) => <div key={`${h.kind}-${i}`} style={{ marginBottom: 3 }}>{h.detail}</div>)}
        </div>
      )}
    />
  ) : null;

  const companyStep = (
    <div>
      <div style={{ color: T.muted, fontSize: 12.5, marginBottom: 6 }}>Which company is it going to?</div>
      <Select filterOption={matchOption}
        showSearch optionFilterProp="label" style={{ width: 460, maxWidth: "100%" }}
        placeholder="Choose the receiving company"
        value={to || undefined} onChange={(v) => { setTo(v); }}
        options={(companies || [])
          .filter((c) => String(c._id) !== String(fromCompanyId))
          .map((c) => ({ value: String(c._id), label: c.companyName || c.name || String(c._id) }))}
      />
      {state.error ? <Alert style={{ marginTop: 10 }} type="error" showIcon message={state.error} /> : null}
    </div>
  );

  const ownerStep = (
    <div>
      <div style={{ color: T.muted, fontSize: 12.5, marginBottom: 6 }}>
        Who owns the project in {p ? p.to.name : "the receiving company"}? People who work there through a second company membership are listed too.
      </div>
      <Select
        showSearch filterOption={false} onSearch={findPeople} style={{ width: 460, maxWidth: "100%" }}
        placeholder="Search by name or email" value={owner || undefined} onChange={setOwner}
        notFoundContent="Nobody in that company matches"
        options={people.map((u) => ({
          value: u._id,
          label: (
            <span>
              {u.name} <span style={{ color: T.muted }}>{u.email}</span>
              {u.companyAdmin ? <Tag style={{ marginLeft: 6 }}>company admin</Tag> : null}
              {u.secondMembership ? <Tag color="purple" style={{ marginLeft: 6 }}>second membership</Tag> : null}
            </span>
          ),
        }))}
      />
      {p && p.newOwner ? <div style={{ marginTop: 8, fontSize: 12.5 }}>Owner afterwards: <b>{p.newOwner.name}</b> ({p.newOwner.email})</div> : null}
    </div>
  );

  const ovCols = [
    { title: "On the entry", key: "on", width: 100, render: (_, d) => (
      <Checkbox checked={d.userId === lead || !!(ov[d.userId] && ov[d.userId].on)} disabled={d.userId === lead} onChange={(e) => setOvOf(d.userId, { on: e.target.checked })} />
    ) },
    { title: "Lead", key: "lead", width: 60, render: (_, d) => <Radio checked={d.userId === lead} onChange={() => setLead(d.userId)} aria-label={`Lead ${d.name}`} /> },
    { title: "Person", key: "p", render: (_, d) => (
      <span>{d.name} <span style={{ color: T.muted }}>{d.email}</span><div style={{ color: T.muted, fontSize: 12 }}>{d.onProject.join(", ")}{d.companyAdmin ? ", company admin" : ""}</div></span>
    ) },
    { title: "Permissions", key: "k", width: 230, render: (_, d) => {
      if (d.userId === lead) return <span style={{ color: T.muted }}>Lead: every power of the project owner</span>;
      const o = ov[d.userId] || { same: true };
      return (
        <Checkbox checked={o.same !== false} onChange={(e) => setOvOf(d.userId, { same: e.target.checked, keys: o.keys && o.keys.length ? o.keys : d.permissions })}>
          Same as today ({d.permissions.length} keys)
        </Checkbox>
      );
    } },
  ];
  const oversightStep = ovExists ? (
    <Alert type="info" showIcon message={`${p.from.name} already oversees this project (lead ${p.oversight.lead}, ${p.oversight.members} member(s)). That entry stays as it is, so this step is skipped.`} />
  ) : (
    <div>
      <div style={{ color: T.muted, fontSize: 12.5, marginBottom: 6 }}>
        {p ? p.from.name : "The departing company"} keeps control of the project through an oversight entry: pick who is on it and the one lead. Each member keeps the keys they hold today unless you change them.
      </div>
      {departing.length ? (
        <Table
          size="small" rowKey="userId" dataSource={departing} columns={ovCols} pagination={false}
          expandable={{
            rowExpandable: (d) => d.userId !== lead && ov[d.userId] && ov[d.userId].same === false,
            expandedRowKeys: departing.filter((d) => d.userId !== lead && ov[d.userId] && ov[d.userId].same === false).map((d) => d.userId),
            showExpandColumn: false,
            expandedRowRender: (d) => (
              <Checkbox.Group
                value={(ov[d.userId] && ov[d.userId].keys) || []}
                onChange={(keys) => setOvOf(d.userId, { keys })}
                options={allKeys.map((k) => ({ value: k, label: k }))}
              />
            ),
          }}
        />
      ) : (
        <Alert type="warning" showIcon message={`${p ? p.from.name : "The departing company"} has nobody on this project to put on an oversight entry. Add its people to the project first.`} />
      )}
    </div>
  );

  const partnerCols = [
    { title: "Person", key: "p", render: (_, d) => <span>{d.name} <span style={{ color: T.muted }}>{d.email}</span></span> },
    { title: "On the partner row as", key: "as", width: 190, render: (_, d) => (
      <Select
        size="small" style={{ width: 170 }} value={(partner[d.userId] && partner[d.userId].as) || "none"}
        onChange={(as) => setPartnerOf(d.userId, { as })}
        options={[{ value: "supervisor", label: "Supervisor" }, { value: "user", label: "User" }, { value: "none", label: "Not on the row" }]}
      />
    ) },
    { title: "Show cost", key: "c", width: 90, render: (_, d) => {
      const x = partner[d.userId] || {};
      return x.as === "supervisor" ? <Switch size="small" checked={!!x.showCost} onChange={(v) => setPartnerOf(d.userId, { showCost: v })} /> : <span style={{ color: T.muted }}>–</span>;
    } },
    { title: "Job title", key: "j", width: 170, render: (_, d) => {
      const x = partner[d.userId] || {};
      return x.as && x.as !== "none" ? <Input size="small" value={x.jobTitle || ""} onChange={(e) => setPartnerOf(d.userId, { jobTitle: e.target.value })} /> : null;
    } },
  ];
  const partnerStep = (
    <div>
      <div style={{ marginBottom: 10 }}>
        <Switch checked={!!partnerOn} onChange={setPartnerOn} />{" "}
        <span>Make {p ? p.from.name : "the departing company"} an active partner on the project once it has moved</span>
      </div>
      {partnerOn ? (
        <div>
          <div style={{ color: T.muted, fontSize: 12.5, marginBottom: 6 }}>
            Its row is written without any mail - no welcome, no first digest, no &quot;Coordination started&quot;. Each person&apos;s hours keep their account rate.
          </div>
          <Table size="small" rowKey="userId" dataSource={departing} columns={partnerCols} pagination={false} />
          {p && p.partner && p.partner.action === "reactivate" ? <div style={{ marginTop: 6, fontSize: 12.5 }}>{p.from.name} had a partner row here before ({p.partner.rowStatus}); it is switched back on with these people, not added twice.</div> : null}
        </div>
      ) : <div style={{ color: T.muted, fontSize: 12.5 }}>{p ? p.from.name : "The departing company"} will not be on the project&apos;s partner roster. Its oversight entry still gives it control.</div>}
    </div>
  );

  const cols = [
    { title: "Collection", dataIndex: "collection", key: "c" },
    { title: "Moving", key: "m", width: 110, align: "right", render: (_, r) => n(r.moving) },
    { title: "Staying with a partner", key: "f", width: 170, align: "right", render: (_, r) => (r.foreign ? <b>{n(r.foreign)}</b> : <span style={{ color: T.muted }}>–</span>) },
    { title: "Kept as recorded", key: "k", width: 130, align: "right", render: (_, r) => (r.kept ? n(r.kept) : <span style={{ color: T.muted }}>–</span>) },
    { title: "No owner recorded", key: "b", width: 140, align: "right", render: (_, r) => (r.blank ? n(r.blank) : <span style={{ color: T.muted }}>–</span>) },
  ];
  const perms = (p && p.permissions) || {};
  const notGranted = perms.notGranted || [];
  const withheld = perms.withheld || [];
  const pricing = (p && p.pricing) || {};
  const diffs = pricing.differences || [];
  const rates = (p && p.rates) || {};
  const lists = (p && p.lists) || {};
  const previewStep = p ? (
    <div>
      <div style={{ marginBottom: 8 }}>
        <b>{p.from.name}</b> → <b>{p.to.name}</b>
        {p.newOwner ? <span style={{ color: T.muted }}> · owned afterwards by {p.newOwner.name} ({p.newOwner.email})</span> : null}
      </div>
      {hazardBox}
      {!p.inline ? (
        <Alert
          type="error" showIcon style={{ marginBottom: 10 }}
          message={`Too large for a web request: ${n(p.totals.moving)} rows to move, the limit is ${n(p.inlineLimit)}`}
          description={p.remedy ? (
            <div>
              <div>{p.remedy.text}</div>
              {Object.entries(p.remedy.files || {}).map(([name, content]) => (
                <div key={name} style={{ marginTop: 6 }}>
                  <div style={{ color: T.muted }}>{name}</div>
                  <pre style={{ fontSize: 11.5, maxHeight: 120, overflow: "auto", background: "#fafafa", padding: 6 }}>{JSON.stringify(content, null, 1)}</pre>
                </div>
              ))}
            </div>
          ) : null}
        />
      ) : null}
      <div style={{ marginBottom: 8 }}>
        <Tag color="blue">{n(p.totals.moving)} rows change hands</Tag>
        {p.totals.foreign ? <Tag color="orange">{n(p.totals.foreign)} stay with a partner</Tag> : null}
        {p.totals.kept ? <Tag>{n(p.totals.kept)} hours rows keep the company they were worked under</Tag> : null}
        {p.totals.blank ? <Tag>{n(p.totals.blank)} carry no owner and follow the project</Tag> : null}
      </div>
      <div style={{ fontSize: 12.5, marginBottom: 8 }}>
        <div><span style={{ color: T.muted }}>Stays as it is: </span>partner rosters, oversight entries, guest links and shares, the model files, and every account.</div>
        <div>
          <span style={{ color: T.muted }}>Pricing check: </span>
          {diffs.length
            ? <b style={{ color: "#b42318" }}>{diffs.length} billable line(s) would cost something different - the transfer refuses</b>
            : <>all {n((pricing.before || []).length)} billable line(s) priced the same on {p.to.name}&apos;s report</>}
        </div>
        <div>
          <span style={{ color: T.muted }}>Rates moving with the project: </span>
          {n((rates.moving || []).length)} record(s){(rates.accountWide || []).length ? `, plus ${n(rates.accountWide.length)} account-wide rate(s) copied onto it` : ""}
        </div>
        <div>
          <span style={{ color: T.muted }}>Copied to {p.to.name} (it has none of its own): </span>
          {[
            (lists.disciplines || []).length ? `${lists.disciplines.length} discipline(s): ${lists.disciplines.map((d) => d.key).join(", ")}` : "",
            (lists.rateCards || []).length ? `${lists.rateCards.length} rate card(s)` : "",
            lists.companySettings ? "send-time settings" : "",
          ].filter(Boolean).join("; ") || "nothing"}
        </div>
        <div>
          <span style={{ color: T.muted }}>Keys {p.to.name} lacks, NOT granted by the transfer: </span>
          {notGranted.length ? notGranted.join(", ") : "none"}
          <span style={{ color: T.muted }}> · already held: {n(perms.alreadyHeld)}</span>
        </div>
        {withheld.length ? <div><span style={{ color: T.muted }}>Withheld (these stay with the platform): </span>{withheld.join(", ")}</div> : null}
        <div>
          <span style={{ color: T.muted }}>Oversight: </span>
          {p.oversight ? (p.oversight.exists ? `existing entry, lead ${p.oversight.lead}` : `created before anything moves, lead ${p.oversight.lead}, ${p.oversight.members} member(s)`) : "none - see above"}
        </div>
        <div>
          <span style={{ color: T.muted }}>{p.from.name} as a partner: </span>
          {p.partner
            ? ({ insert: "added", reactivate: "existing row switched back on", "already-active": "already active, left as it is" }[p.partner.action] || p.partner.action)
              + (p.partner.action !== "already-active" ? ` - supervisors ${p.partner.supervisors.map((s) => s.email).join(", ") || "none"}; users ${p.partner.users.map((u) => u.email).join(", ") || "none"}` : "")
            : "no"}
        </div>
      </div>
      <Table
        size="small" rowKey="collection" loading={state.loading}
        dataSource={[...(p.collections || [])].sort((a, b) => b.moving - a.moving)}
        columns={cols}
        pagination={{ pageSize: 8, size: "small", hideOnSinglePage: true }}
      />
    </div>
  ) : null;

  const confirmStep = p ? (
    <div>
      {hazardBox}
      <div style={{ marginBottom: 6 }}>
        Type the project&apos;s name to confirm: <b>{p.project.name}</b>
      </div>
      <Input style={{ maxWidth: 460 }} value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={p.project.name} aria-label="Project name" />
    </div>
  ) : null;

  const resultView = result ? (
    <div>
      <Alert
        type={result.ok ? "success" : "warning"} showIcon
        message={`${result.project.name} now belongs to ${result.to.name}, owned by ${result.newOwner ? result.newOwner.name : "the named owner"}`}
        description={(
          <div style={{ fontSize: 12.5 }}>
            <div>{n((result.moved || []).reduce((a, m) => a + (m.moved || 0), 0))} rows moved; pricing check: {n(result.pricing && result.pricing.lines)} line(s) priced the same.</div>
            {result.partnerRow ? <div>{result.from.name} as a partner: {result.partnerRow.action}.</div> : null}
            <div>No mail was sent; the project was in migration mode for the run.</div>
            {!result.ok ? <div>Still pointing at {result.from.name}: {(result.verification.leftovers || []).map((l) => `${l.collection} (${n(l.count)})`).join(", ")}</div> : null}
          </div>
        )}
      />
      <div style={{ marginTop: 12 }}>
        <Popconfirm
          title={`Put ${result.project.name} back with ${result.from.name}?`}
          okText="Undo" onConfirm={undo}
        >
          <Button danger loading={undoing}>Undo transfer - return to {result.from.name}</Button>
        </Popconfirm>
      </div>
    </div>
  ) : null;

  const body = [companyStep, ownerStep, oversightStep, partnerStep, previewStep, confirmStep][step];
  const footer = result
    ? [<Button key="c" onClick={onClose}>Close</Button>]
    : [
      <Button key="c" onClick={onClose}>Cancel</Button>,
      step > 0 ? <Button key="b" onClick={() => setStep(step - 1)}>Back</Button> : null,
      step < STEPS.length - 1
        ? <Button key="n" type="primary" disabled={!can[step]} onClick={() => setStep(step + 1)}>Next</Button>
        : <Button key="g" type="primary" danger loading={running} disabled={!can[step]} onClick={go}>Transfer it</Button>,
    ];

  return (
    <Modal
      open={!!project}
      title={project ? `Transfer "${project.title}" to another company` : ""}
      onCancel={onClose}
      width={960}
      footer={footer}
    >
      {!result ? <Steps size="small" current={step} items={STEPS.map((title) => ({ title }))} style={{ marginBottom: 14 }} /> : null}
      {result ? resultView : (
        <div>
          {body}
          {step > 0 && step < 4 && p && p.blocked ? (
            <div style={{ marginTop: 10, color: T.muted, fontSize: 12.5 }}>
              Still needed: {p.hazards.filter((h) => ["no-new-owner", "no-oversight", "partner-unknown", "partner-empty"].includes(h.kind)).map((h) => h.detail).join(" ") || "see the preview"}
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
